import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgStyle } from '@angular/common';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { LoggerService } from '@/core/services/logger.service';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { EtiquetaProfissionalService } from '@/core/api/sicom-api/registro/medico/relatorios/etiqueta-profissional/etiqueta-profissional.service';
import { AssinanteEtiqueta, EtiquetaItem, ReportEtiquetaProfissionalRequest, ReportEtiquetaProfissionalResponse } from '@/core/api/sicom-api/registro/medico/relatorios/etiqueta-profissional/etiqueta-profissional.model';
import { EtiquetaProfissionalJobService } from '@/core/services/etiqueta-profissional-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportEtiquetaProfissionalRequest;
type Resp = ReportEtiquetaProfissionalResponse;

/** Rótulos literais dos 30 tipos (UntEtiquetaProfissional.dfm:181-213). */
const TIPOS_ETIQUETA: { value: number; label: string }[] = [
    { value: 0, label: 'IDENTIFICAÇÃO CARTEIRA PROFISSIONAL' },
    { value: 1, label: 'INSCRIÇÃO SECUNDÁRIA' },
    { value: 2, label: 'INSCRIÇÃO POR TRANSFERÊNCIA' },
    { value: 3, label: 'REINSCRIÇÃO POR TRANSFERÊNCIA' },
    { value: 4, label: 'REINSCRIÇÃO SECUNDÁRIA' },
    { value: 5, label: 'REATIVAÇÃO DE INSCRIÇÃO' },
    { value: 6, label: 'TRANSFERÊNCIA COM TRANSFORMAÇÃO DO TIPO' },
    { value: 7, label: 'MÉDICO MILITAR' },
    { value: 8, label: 'INSCRIÇÃO PROVISÓRIA' },
    { value: 9, label: 'CANCELAMENTO DE INSCRIÇÃO - AUSÊNCIA NO PAÍS' },
    { value: 10, label: 'CANCELAMENTO DE INSCRIÇÃO - ATIVIDADE' },
    { value: 11, label: 'CANCELAMENTO DE INSCRIÇÃO SECUNDÁRIA' },
    { value: 12, label: 'CANCELAMENTO DE MÉDICO EXCLUSIVAMENTE MILITAR' },
    { value: 13, label: 'CANCELAMENTO POR FALECIMENTO' },
    { value: 14, label: 'CANCELAMENTO DE INSCRIÇÃO - NÃO APRESENTAÇÃO DIPLOMA' },
    { value: 15, label: 'VISTO PROVISÓRIO' },
    { value: 16, label: 'AVERBAÇÃO DE NOME' },
    { value: 17, label: 'AVERBAÇÃO DE NACIONALIDADE' },
    { value: 18, label: 'REGISTRO DE MÉDICO DO TRABALHO' },
    { value: 19, label: 'TRANSFORMAÇÃO DE INSCRIÇÃO PRIMÁRIA EM SECUNDÁRIA' },
    { value: 20, label: 'TRANSFORMAÇÃO DE INSCRIÇÃO PROVISÓRIA EM DEFINITIVA' },
    { value: 21, label: 'REATIVAÇÃO DE INSCRIÇÃO' },
    { value: 22, label: 'REATIVAÇÃO DE INSCRIÇÃO RETORNO PAÍS' },
    { value: 23, label: 'TRANSFERÊNCIA COM TRANSFORMAÇÃO DE TIPO DE INSCRIÇÃO' },
    { value: 24, label: 'REGULARIZAÇÃO DA INSCRIÇÃO COM TRANSFORMAÇÃO DE PROVISÓRIA PARA DEFINITIVA' },
    { value: 25, label: 'REATIVAÇÃO INSCRIÇÃO RETORNO ATIVIDADES' },
    { value: 26, label: 'SECUNDÁRIA COM TRANSFORMAÇÃO' },
    { value: 27, label: 'INSCRIÇÃO PROVISÓRIA POR TRANSFERÊNCIA' },
    { value: 28, label: 'INSCRIÇÃO PROVISÓRIA SECUNDÁRIA' },
    { value: 29, label: 'REGISTRO DE DIPLOMA' }
];

const TIPO_DIPLOMA = 29;
const TIPO_CARTEIRA = 0;

/** Controles de uma posição (célula) da folha de etiquetas. */
interface PosicaoControls {
    posicao: FormControl<number | null>;
    ativo: FormControl<boolean | null>;
    crm: FormControl<string | null>;
    uf: FormControl<string | null>;
    numeroControle: FormControl<string | null>;
    registrar: FormControl<boolean | null>;
    observacao: FormControl<string | null>;
    dataEmissao: FormControl<string | null>;
    ufOrigem: FormControl<string | null>;
}

/** Controles do formulário raiz do assistente Etiqueta Profissional. */
interface EtiquetaProfissionalControls {
    tipoDocumento: FormControl<number | null>;
    incluirAssinatura: FormControl<boolean | null>;
    assinante1: FormControl<AssinanteEtiqueta | null>;
    assinante2: FormControl<AssinanteEtiqueta | null>;
    posicoes: FormArray<FormGroup<PosicaoControls>>;
}

/**
 * Assistente "Etiqueta Profissional" (Médico) — monta uma folha A4 de etiquetas.
 *
 * O operador escolhe o tipo (0..29), a UF do órgão, marca posições numa grade
 * (2x2 para tipos != 29, 2x6 para Diploma), informa CRM+UF por posição (o backend
 * resolve os dados de cada médico), opcionalmente nº de controle / observação /
 * data / UF de origem, e seleciona o(s) assinante(s). Gera via job assíncrono.
 *
 * Herda `RelatorioFormBase`: a orquestração de export/polling/download/erro vem da
 * base; aqui restam a montagem da grade, a validação por posição e o `buildRequest`.
 */
@Component({
    selector: 'app-etiqueta-profissional',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, NgStyle, Button, ReactiveFormsModule, InputTextModule, CheckboxModule, SelectModule, SelectEstado, RelatorioContainer, CanDirective],
    templateUrl: './etiqueta-profissional.html'
})
export class ReportEtiquetaProfissionalComponent extends RelatorioFormBase<Req, Resp> implements OnInit {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.medicos.relatorios.gerar;

    private readonly logger = inject(LoggerService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly etiquetaService = inject(EtiquetaProfissionalService);
    private readonly pessoaService = inject(PessoaService);
    readonly etiquetaJobService = inject(EtiquetaProfissionalJobService);

    readonly job$ = this.etiquetaJobService.job$;
    readonly tipos = TIPOS_ETIQUETA;

    form: FormGroup<EtiquetaProfissionalControls> = this.formBuilder.group<EtiquetaProfissionalControls>({
        tipoDocumento: new FormControl<number | null>(0, Validators.required),
        incluirAssinatura: new FormControl<boolean | null>(false),
        assinante1: new FormControl<AssinanteEtiqueta | null>(null),
        assinante2: new FormControl<AssinanteEtiqueta | null>(null),
        posicoes: new FormArray<FormGroup<PosicaoControls>>([])
    });

    ufOrgao = 'BR';
    ufOrgaoTocada = false;

    readonly assinantes = signal<AssinanteEtiqueta[]>([]);
    readonly loadingAssinantes = signal(false);

    get tipoDocumento(): number {
        return Number(this.form.controls.tipoDocumento.value ?? 0);
    }

    get isDiploma(): boolean {
        return this.tipoDocumento === TIPO_DIPLOMA;
    }

    get isCarteira(): boolean {
        return this.tipoDocumento === TIPO_CARTEIRA;
    }

    /** Assinante 1 aparece para todos menos a Carteira (0). */
    get mostrarAssinante1(): boolean {
        return !this.isCarteira;
    }

    /** Assinante 2 só para a Etiqueta Profissional (1..28). */
    get mostrarAssinante2(): boolean {
        return this.tipoDocumento >= 1 && this.tipoDocumento <= 28;
    }

    get posicoes(): FormArray<FormGroup<PosicaoControls>> {
        return this.form.controls.posicoes;
    }

    protected get jobFacade(): RelatorioJobFacade {
        return this.etiquetaJobService;
    }

    constructor() {
        super();
        this.reconstruirGrade();
        this.form.controls.tipoDocumento.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.reconstruirGrade();
            if (!this.mostrarAssinante1) this.form.controls.assinante1.setValue(null);
            if (!this.mostrarAssinante2) this.form.controls.assinante2.setValue(null);
        });
    }

    override ngOnInit(): void {
        super.ngOnInit();
        const pessoa = this.pessoaService.getPessoa();
        const uf = pessoa?.uf || this.pessoaService.getPessoaReference()?.uf || '';
        if (uf) {
            this.ufOrgao = uf.toUpperCase();
            this.carregarAssinantes();
        }
    }

    // ------------------------------------------------------------------ grade

    private reconstruirGrade(): void {
        const total = this.isDiploma ? 12 : 4;
        const atual = this.posicoes.length;
        if (atual === total) return;
        this.posicoes.clear();
        for (let n = 1; n <= total; n += 1) {
            this.posicoes.push(this.novaPosicao(n));
        }
    }

    private novaPosicao(posicao: number): FormGroup<PosicaoControls> {
        return this.formBuilder.group<PosicaoControls>({
            posicao: new FormControl<number | null>(posicao),
            ativo: new FormControl<boolean | null>(false),
            crm: new FormControl<string | null>(''),
            uf: new FormControl<string | null>('BR'),
            numeroControle: new FormControl<string | null>(''),
            registrar: new FormControl<boolean | null>(false),
            observacao: new FormControl<string | null>(''),
            dataEmissao: new FormControl<string | null>(''),
            ufOrigem: new FormControl<string | null>('')
        });
    }

    togglePosicao(grupo: AbstractControl): void {
        grupo.get('ativo')?.setValue(!grupo.get('ativo')?.value);
    }

    // ----------------------------------------------------- mapa visual da folha A4
    // Coordenadas em % da folha A4 (210x297mm), do DESIGN-ETIQUETA-PROFISSIONAL.md.

    /** 2x2 (Carteira/Profissional): posição -> canto superior-esquerdo (% da folha). */
    private static readonly COORD_4UP: Record<number, { left: number; top: number }> = {
        1: { left: 8.31, top: 7.13 },
        2: { left: 58.46, top: 7.13 },
        3: { left: 8.31, top: 58.44 },
        4: { left: 58.46, top: 58.44 }
    };
    private static readonly SIZE_4UP = { width: 27.09, height: 28.33 };

    /** 2x6 (Diploma): colunas (esq/dir) e linhas (topo) em % da folha. */
    private static readonly COL_12UP = { esquerda: 3.02, direita: 49.51 };
    private static readonly ROW_12UP = [4.99, 20.22, 36.08, 51.31, 66.55, 81.78];
    private static readonly SIZE_12UP = { width: 38.3, height: 8.64 };

    /** Posição/tamanho da célula da etiqueta na folha (fiel ao layout físico). */
    private coordEtiqueta(posicao: number): { left: number; top: number; width: number; height: number } {
        const C = ReportEtiquetaProfissionalComponent;
        if (this.isDiploma) {
            // numeração: coluna esquerda = 1,3,5,7,9,11; direita = 2,4,6,8,10,12
            const linha = Math.floor((posicao - 1) / 2);
            const left = posicao % 2 === 1 ? C.COL_12UP.esquerda : C.COL_12UP.direita;
            return { left, top: C.ROW_12UP[linha] ?? 0, width: C.SIZE_12UP.width, height: C.SIZE_12UP.height };
        }
        const c = C.COORD_4UP[posicao] ?? { left: 0, top: 0 };
        return { left: c.left, top: c.top, width: C.SIZE_4UP.width, height: C.SIZE_4UP.height };
    }

    /** Estilo inline da célula: coordenadas + preenchimento com a cor primária quando ativa. */
    estiloEtiqueta(grupo: AbstractControl): Record<string, string> {
        const { left, top, width, height } = this.coordEtiqueta(Number(grupo.get('posicao')?.value));
        const ativo = !!grupo.get('ativo')?.value;
        return {
            position: 'absolute',
            left: `${left}%`,
            top: `${top}%`,
            width: `${width}%`,
            height: `${height}%`,
            backgroundColor: ativo ? 'var(--p-primary-color)' : 'transparent',
            color: ativo ? 'var(--p-primary-contrast-color)' : 'var(--p-text-muted-color)',
            borderColor: ativo ? 'var(--p-primary-color)' : 'var(--p-content-border-color)'
        };
    }

    // --------------------------------------------------------------- assinantes

    onUfOrgaoChange(uf: string): void {
        this.ufOrgao = (uf || '').toUpperCase();
        this.form.controls.assinante1.setValue(null);
        this.form.controls.assinante2.setValue(null);
        this.carregarAssinantes();
    }

    carregarAssinantes(): void {
        const uf = this.ufOrgao;
        if (!uf || uf === 'BR') {
            this.assinantes.set([]);
            return;
        }
        this.loadingAssinantes.set(true);
        this.etiquetaService.listarAssinantes(uf).subscribe({
            next: (lista) => {
                this.assinantes.set(lista ?? []);
                this.loadingAssinantes.set(false);
            },
            error: (err) => {
                this.logger.error('Erro ao carregar assinantes da etiqueta profissional', err);
                this.assinantes.set([]);
                this.loadingAssinantes.set(false);
            }
        });
    }

    rotuloAssinante(a: AssinanteEtiqueta): string {
        return a.cargo ? `${a.nome} — ${a.cargo}` : a.nome;
    }

    // ------------------------------------------------------------------ ações

    limparFormulario(): void {
        this.form.reset({ tipoDocumento: 0, incluirAssinatura: false, assinante1: null, assinante2: null });
        this.reconstruirGrade();
        this.limparJob();
    }

    /** Valida a grade/posições e monta o request; retorna null (avisando) quando inválido. */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        this.ufOrgaoTocada = true;

        const ativos = this.posicoes.controls.filter((c) => c.controls.ativo.value);
        if (ativos.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Marque ao menos uma posição na grade e informe CRM e UF.' });
            return null;
        }

        const etiquetas: EtiquetaItem[] = [];
        const controles = new Set<string>();
        for (const c of ativos) {
            const crm = Number(c.controls.crm.value);
            const uf = (c.controls.uf.value || '').toUpperCase();
            if (!crm || !uf || uf === 'BR') {
                this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe CRM e UF em todas as posições marcadas.' });
                return null;
            }
            const numeroControle = (c.controls.numeroControle.value || '').trim();
            if (numeroControle) {
                if (controles.has(numeroControle)) {
                    this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'O nº de etiqueta informado está duplicado!' });
                    return null;
                }
                controles.add(numeroControle);
            }
            etiquetas.push({
                posicao: Number(c.controls.posicao.value),
                crm,
                uf,
                numeroControle: numeroControle || undefined,
                registrar: !!c.controls.registrar.value,
                observacao: (c.controls.observacao.value || '').trim() || undefined,
                dataEmissao: (c.controls.dataEmissao.value || '').trim() || undefined,
                ufOrigem: (c.controls.ufOrigem.value || '').trim().toUpperCase() || undefined
            });
        }

        const assinantes: AssinanteEtiqueta[] = [];
        const a1 = this.form.controls.assinante1.value;
        const a2 = this.form.controls.assinante2.value;
        if (this.mostrarAssinante1 && a1) assinantes.push(a1);
        if (this.mostrarAssinante2 && a2) assinantes.push(a2);

        return {
            tipoDocumento: this.tipoDocumento,
            incluirAssinatura: !!this.form.controls.incluirAssinatura.value,
            assinantes,
            etiquetas
        };
    }

    protected exportar(request: Req) {
        return this.etiquetaService.exportarPdf(request);
    }
}
